import {formsStore} from '../../../_legacy/Form/stores/FormsStore';
import {formsStore as newApiFormsStore} from '../../../stores/Form';
import {getLanguage} from '../../GWTBridge/invoke/common';
import {prepareHelpMessage} from '../../GWTBridge/invoke/netForm';
import {getEntityHelp, getFormHelpMessage} from '../../../_legacy/services/helpService';
import {getDataByDocType, getItemDetails, getVersion} from '../../../services/xpertService';
import {DocType} from '../types';
import {getFormHelpText} from '../../../features/Form/actions/help';
import {startNewForm} from '../../../features/Form/actions/screen';
import {helpPopoverRef, NO_FIND_FORM} from '../constants';

export const hasErrorXpertConditions = (xpretData, textHelp, helpContent = null) => {
    //has error from xpert
    if (!xpretData.documents || !xpretData.videos || !xpretData.faqs) {
        return {text: textHelp, hasXpertError: true};
    } else {
        return {...xpretData, text: textHelp, helpContent};
    }
};

export const conditionsForHelpText = async (isNewApi: boolean, formName: string) => {
    if (isNewApi) {
        const textHelp = await getFormHelpText(formName);

        return {
            text: (textHelp as {content: string}).content,
            linkData: textHelp.linkData,
            onLinkClick: ({eName}) => startNewForm(eName)
        };
    }
    return await getLegacyFormHelpText();
};

export const showFormHelp = async () => {
    const currentForm = formsStore.currentForm || newApiFormsStore.currentForm;
    const isNewApi = !!newApiFormsStore.currentForm;
    const formName = formsStore.currentForm?.name || newApiFormsStore.currentForm?.ename;

    if (currentForm) {
        currentForm.helpBook.setShowHelp(true);
        if (!currentForm.helpBook?.formHelp) {
            currentForm.helpBook.setIsLoadingHelpData(true);
            const formHelp = await showHelp({isNewApi, name: formName});
            currentForm.helpBook.setHelpMessage(formHelp);
            currentForm.helpBook.setIsLoadingHelpData(false);
        }
    }
};

export type ShowHelp = {
    isNewApi: boolean;
    name?: string;
    searchString?: string;
};
export const showHelp = async ({isNewApi, name, searchString}: ShowHelp) => {
    const xpretData = await getHelpData({Fname: name, searchString});
    if (!searchString) {
        const textHelp = await conditionsForHelpText(isNewApi, name);
        return hasErrorXpertConditions(xpretData, textHelp);
    }
    return xpretData;
};

const prepareHelpText = (content, links, onLinkClick) => {
    let linkData;
    const text = content.slice(content.indexOf(':') + 1);
    if (links?.length > 0) {
        const titleLength = (formsStore.currentForm.title + ':').length;
        linkData = [];
        let indexSubtraction = 1;
        if (content.substring(content.indexOf(':') + 1, content.indexOf(':') + 3) === '\n\n') {
            indexSubtraction = 2;
        }
        links?.forEach(link => {
            link.from = link.from - titleLength - indexSubtraction;
            linkData.push(link);
        });
    }
    return {text, linkData, onLinkClick};
};

export const getLegacyFormHelpText = async () => {
    const helpDS = await getFormHelpMessage(formsStore.currentForm);
    const {content, linkData, onLinkClick} = prepareHelpMessage(helpDS);

    return prepareHelpText(content, linkData, onLinkClick);
};

export const showEntityHelpMessage = async (exec, anchor, handleLinkClick?) => {
    const helpDS = await getEntityHelp(exec);
    const {content, linkData, onLinkClick} = prepareHelpMessage(helpDS);
    const linkClick = linkData => {
        handleLinkClick && handleLinkClick();
        onLinkClick(linkData);
    };
    helpPopoverRef.current.showHelpPopover({
        title: 'Help',
        content,
        linkData,
        onLinkClick: linkClick,
        anchor
    });
};

export type HelpData = {Fname?: string; searchString?: string};

const getHelpData = async ({Fname, searchString}: HelpData) => {
    const [faqs, documents, videos] = await Promise.all([
        getFAQs(Fname, searchString),
        getDocuments(Fname, searchString),
        getVideos(Fname, searchString)
    ]);

    return {faqs, documents, videos};
};
export const getFAQs = async (Fname?: string, searchString?: string) => {
    const res = await getDataByDocType({formName: Fname, docType: DocType.FAQ, searchString});
    return res?.data
        ?.slice()
        ?.sort((a, b) => a.priority - b.priority)
        .map(faq => ({id: faq.id, text: faq.title}));
};

export const getDocuments = async (Fname?: string, searchString?: string) => {
    const res = await getDataByDocType({formName: Fname, docType: DocType.Document, searchString});
    return res?.data
        ?.slice()
        ?.sort((a, b) => a.priority - b.priority)
        .map(doc => ({id: doc.id, text: doc.title, fileUrl: doc.fileUrl}));
};

export const getVideos = async (Fname?: string, searchString?: string) => {
    const res = await getDataByDocType({formName: Fname, docType: DocType.Video, searchString});
    return res?.data
        ?.slice()
        ?.sort((a, b) => a.priority - b.priority)
        .map(video => ({id: video.id, text: video.title, fileUrl: video.fileUrl}));
};

export const getDetails = async (ID: string) => {
    const res = await getItemDetails(ID);
    return res ? {html: res.html, title: res.title} : res;
};

export const openInXpertByFormName = () => {
    const lang = getLanguage() === 1 ? 'Hebrew' : 'English';
    const formName = encodeURI(formsStore.currentForm?.name || newApiFormsStore.currentForm?.ename);
    const formQuery = formName !== NO_FIND_FORM ? '&enames=' + formName : '';

    window.open(
        `https://support.priority-software.com/#/kb?pageSize=10&languages=${lang}&versions=${getVersion()}${formQuery}`,
        '_blank'
    );
};
export const openInXpertByDocType = (docType, query?) => {
    const lang = getLanguage() === 1 ? 'Hebrew' : 'English';
    const formName = encodeURI(formsStore.currentForm?.name || newApiFormsStore.currentForm?.ename);
    const formQuery = formName !== NO_FIND_FORM ? '&enames=' + formName : '';
    const searchText = query && docType === 'FAQ' ? `&searchString=${query}` : '';
    window.open(
        `https://support.priority-software.com/#/kb?pageSize=10&languages=${lang}&docTypes=${docType}&versions=${getVersion()}${formQuery}${searchText}`,
        '_blank'
    );
};

export const openInXpertById = id => {
    const lang = getLanguage() === 1 ? 'Hebrew' : 'English';
    window.open(`https://support.priority-software.com/#/kb/${id}/${lang}`, '_blank');
};
